import { Fragment, useContext, useEffect, useState } from 'react';
import Select, { MenuProps, components } from 'react-select';
import { useField, useFormikContext } from 'formik';
//import { handleKeyPress } from '@rabbit/components/shared-ui';
import { InputTypeAutoCompleteConsumerShape } from '@rabbit/elements/shared-types';
import { useDebounce } from '../../../utils/hooks/useDebounce';
import { useGetConsumerList } from '@rabbit/bizproc/react';
import { generateSelectStyles } from '../../../utils/consts';
import { CORPORATE } from '@rabbit/sage/utils/consts';
import Bus from '@rabbit/bus';
import { AppContext } from '@rabbit/app-context';
import { useTranslation } from 'react-i18next';
import { useGetMySagePersonas } from '@rabbit/data/portal';

export interface InputAutoCompleteConsumerProps {
  name: string;
  settings: InputTypeAutoCompleteConsumerShape;
  onChange?: (...args: any) => any; // todo: review
}

type SearchType = 'customer' | 'vendable' | 'holdings';

interface OptionShape {
  id: string;
  label: string;
  item: object;
  value: string;
}

export function InputAutoCompleteConsumer({
  name,
  settings,
  onChange: defaultOnChange,
}: InputAutoCompleteConsumerProps) {
  const { setFieldValue } = useFormikContext();
  const { config } = useContext(AppContext);
  const { t } = useTranslation();
  const { installerPersona } = useGetMySagePersonas();
  const {
    placeholder,
    isMulti = false,
    isClearable = true,
    disabled = false,
    customButton,
    token,
    type,
    ...props
  } = settings;

  const {
    consumerList,
    getConsumersByEmail,
    getConsumersByFirstName,
    getConsumersByLastName,
    searchHoldings,
    isUsingHoldingProxy,
  } = useGetConsumerList(
    token ?? null,
    type,
    config.CUSTOMER.CORPORATE_ACCOUNT,
    installerPersona?.personaId!
  );
  const [field, meta] = useField(name);

  const [options, setOptions] = useState<OptionShape[]>([]);
  const [inputText, setInputText] = useState('');
  // If installerPersona is not null and we're using holdingproxy data, default to 'holdings' search type
  const [searchType, setSearchType] = useState<SearchType>(
    isUsingHoldingProxy ? 'holdings' : 'customer'
  );
  // Note: Debouncing is actually not currently required here as the consumerList query is just being filtered.
  // Setting it to 100ms just to maintain the rest of the code as is, since this doesn't have a major performance impact and
  // it may be handled through an api in the near future. - DC

  const debouncedInput = useDebounce(inputText, 100);
  const [isLoadingResults, setIsLoadingResults] = useState(false);

  let errorField = false;
  if (meta?.error) errorField = true;

  function onChange(option: any) {
    if (isMulti) {
      void setFieldValue(
        name,
        option ? option.map((item: { value: any }) => item.value) : []
      );
    } else {
      void setFieldValue(name, option?.value ? option.value : '');
    }
    defaultOnChange && defaultOnChange(option);
  }

  const getValue = () => {
    if (options) {
      return isMulti
        ? options.filter(
            (option: { value: any }) => field.value.indexOf(option.value) >= 0
          )
        : options.find(
            (option: { value: any }) => option.value === field.value
          );
    } else {
      return isMulti ? [] : '';
    }
  };

  useEffect(() => {
    const generateOptions = async () => {
      let data = [] as any[];

      // Search based on the selected search type
      if (searchType === 'customer') {
        // For customer search, we can search by email, first name, or last name
        const emailResults = getConsumersByEmail(debouncedInput);
        const firstNameResults = getConsumersByFirstName(debouncedInput);
        const lastNameResults = getConsumersByLastName(debouncedInput);

        // Combine results and remove duplicates
        const allResults = [
          ...(emailResults || []),
          ...(firstNameResults || []),
          ...(lastNameResults || []),
        ];
        const uniqueResults = allResults.filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.docid === item.docid)
        );

        data = uniqueResults;
      } else if (searchType === 'holdings') {
        // Search holdings by serial, registration_number, etc.
        try {
          // Get the active tenant link from localStorage
          const activeTenantLink = localStorage.getItem('activeTenant');
          if (activeTenantLink) {
            data = await searchHoldings(debouncedInput, activeTenantLink);
          } else {
            console.error('No active tenant found');
            data = [];
          }
        } catch (error) {
          console.error('Error fetching holdings:', error);
          data = [];
        }
      }

      if (data?.length) {
        const newOptions: OptionShape[] = [];
        data.map((item) => {
          console.log('item', item);
          if (searchType === 'customer' && item.email) {
            newOptions.push({
              id: item.docid,
              label:
                type === CORPORATE
                  ? item.business_name + ' (' + item.email + ')'
                  : item.fullname + ' (' + item.email + ')',
              value: item.email,
              item: item,
            });
          } else if (searchType === 'holdings' && item.title) {
            newOptions.push({
              id: item.consumer,
              label:
                type === CORPORATE
                  ? item.business_name + ' (' + item.consumer_email + ')'
                  : item.consumer_name +
                    ' (' +
                    item.consumer_email +
                    ' - ' +
                    item.title +
                    ' - ' +
                    item.serial +
                    ' - ' +
                    item.mpn +
                    ' - ' +
                    item.brand +
                    ')',
              value: item.consumer_email,
              item: {
                docid: item.consumer,
                email: item.consumer_email,
                fullname: item.consumer_name,
                serial: item.serial,
                title: item.title,
                mpn: item.mpn,
                brand: item.brand,
              },
            });
          }
        });
        setOptions(newOptions);
      }
      setIsLoadingResults(false);
    };

    if (debouncedInput.length) {
      setIsLoadingResults(true);
      generateOptions().catch((error) => {
        console.error('Error generating options:', error);
        setIsLoadingResults(false);
        setOptions([]);
      });
    } else if (settings?.options && settings?.options?.length > 0) {
      setOptions(settings?.options as OptionShape[]);
    } else setOptions([]);
  }, [debouncedInput, type, searchType]);

  const onInputChange = (e: string) => {
    setInputText(e);
  };

  const CustomMenu = (props: MenuProps<any>) => {
    return (
      <Fragment>
        <components.Menu {...props}>
          <div className="font-nunito text-base font-semibold text-gray-900">
            {searchType === 'customer'
              ? type === CORPORATE
                ? t('inputAutoCompleteConsumer.business', 'Business')
                : t('inputAutoCompleteConsumer.customers', 'Customers')
              : searchType === 'vendable'
              ? t('inputAutoCompleteConsumer.products', 'Products')
              : t('inputAutoCompleteConsumer.holdings', 'Registered products')}
          </div>
          {props.children}
          {customButton && (
            <div className="mt-2 w-full border-t border-gray-300 pt-2">
              {customButton}
            </div>
          )}
        </components.Menu>
      </Fragment>
    );
  };

  interface ItemData {
    fullname?: string;
    email?: string;
    business_name?: string;
    phone?: string;
    // Vendable fields
    title?: string;
    brand?: string;
    mpn?: string;
    // Holding fields
    consumer_email?: string;
    consumer?: string;
    consumer_name?: string;
    serial?: string;
    registration_number?: string;
    // Additional fields for improved display
    docid?: string;
  }

  interface OptionData {
    item?: ItemData;
  }

  const CustomOption = (props: any) => {
    const {
      innerProps,
      isDisabled,
      data: { item },
    }: { innerProps: any; isDisabled: boolean; data: OptionData } = props;

    if (!isDisabled && item) {
      return (
        <div
          {...innerProps}
          className="font-nunito focus-within:bg-primary-200 active:bg-primary-200 hover:!bg-primary-200 my-1 !cursor-pointer overflow-hidden rounded !bg-gray-50 p-2 text-gray-900"
        >
          {searchType === 'customer' && (
            <>
              <div className="font-nunito text-base font-semibold text-gray-700">
                {type === CORPORATE
                  ? item.business_name
                    ? item.business_name
                    : '-'
                  : item.fullname
                  ? item.fullname
                  : '-'}
              </div>
              <div className="font-nunito text-sm text-gray-500">
                {item.email ? item.email : '-'}
              </div>
              {item.phone && (
                <div className="font-nunito text-sm text-gray-500">
                  {item.phone}
                </div>
              )}
            </>
          )}
          {searchType === 'holdings' && (
            <div className="flex flex-col space-y-1">
              <div className="flex items-center justify-between">
                <div className="font-nunito text-base font-semibold text-gray-700">
                  {item.email ? item.email : '-'}
                </div>
              </div>
              {item.fullname && (
                <div className="font-nunito text-sm text-gray-500">
                  {t('inputAutoCompleteConsumer.customerColon', 'Customer:')}{' '}
                  {item.fullname}
                </div>
              )}
              <div className="mt-1 rounded-md bg-gray-100 p-2">
                <div className="font-nunito text-sm font-medium text-gray-800">
                  {item.title}
                </div>
                <div className="flex flex-wrap gap-2">
                  {item.brand && (
                    <div className="font-nunito text-xs text-gray-600">
                      <span className="font-semibold">
                        {t('inputAutoCompleteConsumer.brandColon', 'Brand:')}
                      </span>{' '}
                      {item.brand}
                    </div>
                  )}
                  {item.mpn && (
                    <div className="font-nunito text-xs text-gray-600">
                      <span className="font-semibold">
                        {t('inputAutoCompleteConsumer.mpnColon', 'MPN:')}
                      </span>{' '}
                      {item.mpn}
                    </div>
                  )}
                  {item.serial && (
                    <div className="font-nunito text-xs text-gray-600">
                      <span className="font-semibold">
                        {t('inputAutoCompleteConsumer.serialColon', 'Serial:')}
                      </span>{' '}
                      {item.serial}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div>
      {config.HOLDINGS.REGISTRATION.ADVANCED_SEARCH && !isUsingHoldingProxy && (
        <div className="mb-4">
          <div
            className="flex space-x-4"
            role="radiogroup"
            aria-label="Search by"
          >
            <div
              onClick={() => setSearchType('customer')}
              className={`relative flex cursor-pointer rounded-lg border border-gray-300 px-3 py-2 shadow-md focus:outline-none ${
                searchType === 'customer'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-900'
              }`}
            >
              <div className="flex items-center">
                <div className="text-sm">
                  <p
                    className={`font-medium ${
                      searchType === 'customer' ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    {t('inputAutoCompleteConsumer.customer', 'Customer')}
                  </p>
                </div>
                <input
                  type="radio"
                  name="searchType"
                  value="customer"
                  checked={searchType === 'customer'}
                  onChange={() => setSearchType('customer')}
                  className="sr-only"
                />
              </div>
            </div>

            <div
              onClick={() => setSearchType('holdings')}
              className={`relative flex cursor-pointer rounded-lg border border-gray-300 px-3 py-2 shadow-md focus:outline-none ${
                searchType === 'holdings'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-900'
              }`}
            >
              <div className="flex items-center">
                <div className="text-sm">
                  <p
                    className={`font-medium ${
                      searchType === 'holdings' ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    {t(
                      'inputAutoCompleteConsumer.registrations',
                      'Registrations'
                    )}
                  </p>
                </div>
                <input
                  type="radio"
                  name="searchType"
                  value="holdings"
                  checked={searchType === 'holdings'}
                  onChange={() => setSearchType('holdings')}
                  className="sr-only"
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <Select
        {...field}
        {...props}
        options={options}
        isMulti={isMulti}
        classNames={generateSelectStyles(errorField)}
        //unstyled
        placeholder={
          !consumerList.data
            ? t('inputAutoCompleteConsumer.loading', 'Loading...')
            : isUsingHoldingProxy
            ? t(
                'inputAutoCompleteConsumer.searchByConsumerNameEmail',
                'Search by consumer name or email...'
              )
            : searchType === 'customer'
            ? t(
                'inputAutoCompleteConsumer.searchByNameEmailPhone',
                'Search by name, email, or phone...'
              )
            : searchType === 'vendable'
            ? t(
                'inputAutoCompleteConsumer.searchByProductNameBrandModel',
                'Search by product name, brand, or model...'
              )
            : t(
                'inputAutoCompleteConsumer.searchBySerialRegistrationBrandMPM',
                'Search by serial number, registration number, brand, MPM...'
              )
        }
        defaultValue={field.value}
        maxMenuHeight={200}
        components={
          // settings.dropDown === 'disabled'
          //   ? {
          //       DropdownIndicator: () => null,
          //       IndicatorSeparator: () => null,
          //     }
          //   : undefined
          {
            DropdownIndicator: () => null,
            IndicatorSeparator: () => null,
            Menu: CustomMenu,
            Option: CustomOption,
          }
        }
        value={
          options
            ? isMulti
              ? options.find(
                  (option: { value: any }) => option.value === field.value
                )
              : getValue()
            : ''
        }
        //error
        //onKeyDown={handleKeyPress}
        onChange={onChange}
        onInputChange={onInputChange}
        isClearable={isClearable}
        isDisabled={disabled || !consumerList.data}
        noOptionsMessage={({ inputValue }) =>
          !inputValue
            ? t('inputAutoCompleteConsumer.typeSomething', 'Type something...')
            : inputText === debouncedInput && !isLoadingResults
            ? t('inputAutoCompleteConsumer.noResultsFound', 'No results found')
            : t('inputAutoCompleteConsumer.loading', 'Loading...')
        }
      />
    </div>
  );
}

export default InputAutoCompleteConsumer;
